import { Container, Box, Flex, Grid } from 'components/box';
import {
  SectionHeading,
  SmallHeader,
  Text,
  ParagraphText,
} from 'components/typography';
import { css } from '@emotion/react';
import resourceUrl from '../util/make-url';
import { prepMetaTags } from '../util/seo';
import config from '../util/load-config';
import { GetStaticPropsResult } from 'next';
import { PageProps } from 'types/types';
import { breakpointsPx } from 'themes/onedayonly';
import { Link } from 'components/configurable-routing';

const ICON_WIDTH = 260;

interface CategoryIcon {
  id: string;
  label: string;
  color: string;
  url: string;
}

const categoryIcons: CategoryIcon[] = [
  {
    id: 'apparel-and-accessories',
    label: 'Apparel & Accessories',
    color: '#272727',
    url: resourceUrl('odo/black-friday/apparel-and-accessories.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'home-and-garden',
    label: 'Home & Garden',
    color: '#8BC52F',
    url: resourceUrl('odo/black-friday/home-and-garden.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'food-beverages',
    label: 'Food & Beverages',
    color: '#E50E29',
    url: resourceUrl('odo/black-friday/food-beverages.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'health-and-beauty',
    label: 'Health & Beauty',
    color: '#E50E62',
    url: resourceUrl('odo/black-friday/health-and-beauty.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'electronics',
    label: 'Electronics',
    color: '#0093D0',
    url: resourceUrl('odo/black-friday/electronics.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'furniture',
    label: 'Furniture',
    color: '#00B1B0',
    url: resourceUrl('odo/black-friday/furniture.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'sporting-goods',
    label: 'Sporting Goods',
    color: '#F36E21',
    url: resourceUrl('odo/black-friday/sporting-goods.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'luggage-and-bags',
    label: 'Luggage & Bags',
    color: '#3A3B46',
    url: resourceUrl('odo/black-friday/luggage-and-bags.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'animals-and-pets',
    label: 'Animals & Pet Supplies',
    color: '#BA1C8B',
    url: resourceUrl('odo/black-friday/animals-and-pets.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'hardware',
    label: 'Hardware',
    color: '#FF9D64',
    url: resourceUrl('odo/black-friday/hardware.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'baby-and-toddler',
    label: 'Baby & Toddler',
    color: '#7ECBEB',
    url: resourceUrl('odo/black-friday/baby-and-toddler.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'cameras-and-optics',
    label: 'Camera & Optics',
    color: '#696B81',
    url: resourceUrl('odo/black-friday/cameras-and-optics.svg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
];

const BlackFriday = () => (
  <Container py={3}>
    <Box>
      <picture>
        <source
          srcSet={resourceUrl('odo/black-friday/banners-2024/mobile.jpg', {
            isAssetUrl: true,
            width: 500,
          })}
          media={`(max-width: ${breakpointsPx[0] - 1}px)`}
        />
        <source
          srcSet={resourceUrl('odo/black-friday/banners-2024/tablet.jpg', {
            isAssetUrl: true,
            width: 900,
          })}
          media={`(min-width: ${breakpointsPx[0]}px) and (max-width: ${
            breakpointsPx[1] - 1
          }px)`}
        />
        <img
          css={theme => css`
            border-radius: ${theme.space[2]}px;
            width: 100%;
          `}
          src={resourceUrl('odo/black-friday/banners-2024/desktop.jpg', {
            width: 1400,
            isAssetUrl: true,
          })}
          alt="Black Friday Banner"
        />
      </picture>
    </Box>
    <Box>
      <SmallHeader mt={[3, 4]}>
        OneDayOnly Black Friday: Unreal Deals
      </SmallHeader>
      <ParagraphText width={['95%', null, '100%']} mb={2}>
        As always, OneDayOnly is bringing the heat this Black Friday, with
        Unreal Deals dropping from the <strong>25th of November</strong> to the
        <strong> 1st of December!</strong> We&apos;re talking discounts anywhere
        from 60% all the way to 100% OFF! Yep, you read that right - some of
        these Black Friday deals are literally FREE! How it works is simple:
        every day from the 25th of November, at a random time, we will have one
        (or more) of these Unreal Deals ready to drop. You&apos;ll have to have
        our app downloaded and push notifications enabled so you can get the
        heads up. Once a Black Friday Unreal Deal has dropped, it&apos;s down to
        fastest fingers first! Don&apos;t miss out!
      </ParagraphText>

      <SmallHeader mt={[3, 4]}>What is Black Friday?</SmallHeader>
      <ParagraphText width={['95%', null, '100%']} mb={2}>
        Traditionally, Black Friday was the name given to the day following
        Thanksgiving in the US, but it quickly became something of a trend to
        mark the beginning of the festive shopping season. On this day (if not
        all week long), many retailers entice shoppers with incredible deals and
        discounts on all the stuff people might have been waiting to buy all
        year long.
      </ParagraphText>

      <ParagraphText width={['95%', null, '100%']}>
        OneDayOnly is no different, and while we are already known for offering
        unbeatable deals on everything under the sun, we certainly like to make
        a lot of noise and hoo-haa for Black Friday, allowing our customers to
        feel like an even BIGGER deal than ever before!
      </ParagraphText>

      <SmallHeader mt={[3, 4]}>Awesome Appliances</SmallHeader>
      <ParagraphText width={['95%', null, '100%']}>
        OneDayOnly doesn&apos;t do half-measures, and we know exactly the kinds
        of products our customers are most interested in, especially when it
        comes to Black Friday. Large appliances are by far the most sought-after
        deals, which is why we run Black Friday TV deals, air fryer Black Friday
        deals, and even incredible discounts on things like Black Friday fridge
        deals, microwaves, ovens and literally anything else you might want for
        your kitchen, living area or bedroom.
      </ParagraphText>

      <ParagraphText width={['95%', null, '100%']} mt={[1, 2]}>
        No room in the house would be complete without adorning it with a few
        Black Friday furniture deals, with stuff on offer including beds,
        mattresses, headboards, lounge suits and even patio furniture - perfect
        for those hot December braai days and nights.
      </ParagraphText>

      <SmallHeader mt={[3, 4]}>Fabulous Fashion</SmallHeader>
      <ParagraphText width={['95%', null, '100%']}>
        Fashions may come and go, but one thing is for sure, and that is that
        Black Friday is the ideal time to find incredible deals on everything
        from running shoes and sneakers to leather jackets, jeans, jerseys and
        more. From popular brands like Crocs and New Balance to Pringle and
        more, we have Black Friday specials on everything you could possibly
        need, whether you&apos;re looking to rock some fresh kit at the gym, or
        impress your friends out on the town.
      </ParagraphText>

      <ParagraphText width={['95%', null, '100%']} mt={[1, 2]}>
        No outfit would be complete without smelling great, which is why we run
        incredible perfume Black Friday deals, for both men and women. With so
        many undeniably fabulous names to look out for, we recommend logging in
        every day and perusing the Black Friday catalogue of deals on offer that
        day. You&apos;re sure to find something you like - it just makes scents!
      </ParagraphText>

      <SmallHeader mt={[3, 4]}>All Hands on Tech</SmallHeader>
      <ParagraphText width={['95%', null, '100%']}>
        Many people consider Cyber Monday the best time to look for deals on all
        things related to tech and appliances, but traditionally the day is
        supposed to be the one on which people get the best deals online as
        opposed to in-store. Since OneDayOnly is already online, we just keep on
        giving people what they want anyway, and extend the Black Friday madness
        a little longer. This is the best time to shop for things like Black
        Friday phone specials (whether you prefer Apple or Android, we have
        something for you!) We also run deals on charging cables, wireless
        charging docs and more.
      </ParagraphText>

      <ParagraphText width={['95%', null, '100%']} mt={[1, 2]}>
        Gamers can keep an eye out for PS5 Black Friday deals, along with such
        gadgets as controllers, headsets, charging stations and more. But
        that&apos;s not all, Black Friday and Cyber Monday are the ideal times
        to look out for everything from UPS devices, routers, PC components and
        more.
      </ParagraphText>
      <SmallHeader mt={[3, 4]}>Beat the Malls and Shop Online</SmallHeader>
      <ParagraphText width={['95%', null, '100%']}>
        Look, life gets busy and malls get crowded, so shopping online is no
        longer seen as a luxury, but a necessity. A way of life, even! It only
        makes sense then that you would want to get your Black Friday shopping
        done from the comfort of your home - or on the go with the OneDayOnly
        app! If you haven&apos;t done so already, we recommend downloading it
        right now and enabling push notifications, so that you can be ever in
        the loop regarding not just Black Friday catalogue deals, but everything
        else! Black Friday is a particularly exciting time at OneDayOnly though,
        where you might just be able to bag a deal for anything from 70% to a
        whopping 100% OFF! Yes, that&apos;s right, we&apos;re known for
        literally GIVING stuff away!
      </ParagraphText>
    </Box>

    {/* catagories we sell*/}
    <Box mb={3}>
      <Box mb={4}>
        <SectionHeading textAlign="start" py={3} fontSize="3" marginBottom={0}>
          Shop BIG DEALS from our most popular categories:
        </SectionHeading>
        <Link
          href={config.helpCentreLinks.blackFriday}
          prefetch={false}
          color="primary"
          css={theme => css`
            color: ${theme.colors.primary};
            :hover {
              transition: color 0.3s ease;
              color: ${theme.colors.brightPink};
            }
          `}
        >
          Terms & Conditions
        </Link>
      </Box>
      <Grid
        gridTemplateColumns={['1fr 1fr', '1fr 1fr 1fr', '1fr 1fr 1fr 1fr']}
        gridColumnGap={4}
        gridRowGap={[4, null, 5]}
      >
        {categoryIcons.map(category => (
          <Flex
            key={category.id}
            maxWidth="100%"
            flexDirection="column"
            alignItems="center"
          >
            <img
              css={css`
                width: ${ICON_WIDTH}px;
                max-width: 100%;
                border-radius: 50%;
                background: ${category.color};
              `}
              src={category.url}
              alt={category.label}
            />

            <Text textAlign="center" fontWeight="bold" mt="10px">
              {category.label}
            </Text>
          </Flex>
        ))}
      </Grid>
    </Box>
  </Container>
);

export async function getStaticProps(): Promise<
  GetStaticPropsResult<PageProps>
> {
  const meta = config.seo.meta.pages['black-friday'];

  return {
    props: {
      ...(meta ? { layout: { metaTags: prepMetaTags(meta) } } : {}),
    },
  };
}

export default BlackFriday;
